import { Component, OnInit, ChangeDetectionStrategy, Inject, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { Observable, Subject } from 'rxjs';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { CopyBookmark, CopyBookmarkSuccess, SaveBookmark, SaveBookmarkSuccess, CopyBookmarkFailure } from 'src/app/bookmarks/state/bookmarks.actions';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { flatMap, first, map, takeUntil, filter } from 'rxjs/operators';
import { BookmarkEntryModel } from 'src/app/bookmarks/models/bookmark.entry.model';
import { GetCollections } from 'src/app/organizations/state/organizations.actions';
import { BookmarkGroupModel } from 'src/app/bookmarks/models/bookmark.group.model';
import { SaveModuleDialogComponent } from '../save-module-dialog/save-module-dialog.component';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { StorageService, StorageItemEnum } from 'src/app/shared/storage.service';

export interface AddTeamModelDialog {
  bookmark: BookmarkModel;
}

@Component({
  selector: 'app-add-team-model-dialog',
  templateUrl: './add-team-model-dialog.component.html',
  styleUrls: ['./add-team-model-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddTeamModelDialogComponent implements OnInit, OnDestroy {

  form: FormGroup;
  settings: AddTeamModelDialog;

  @Select(OrganizationsState.getCollections)
  collections$: Observable<CollectionModel[]>;

  @Select(BookmarksState.getCopying)
  copying$: Observable<boolean>;

  ngUnsubscribe = new Subject();
  
  constructor(
    private store: Store,
    private actions: Actions,
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<SaveModuleDialogComponent>,
    private snackBar: MatSnackBar,
    private storageService: StorageService,
    @Inject(MAT_DIALOG_DATA) public data: AddTeamModelDialog
  ) { 
    this.settings = {...data};
    // #TODO: AC, this should happen on first launch
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    if (organization) {
      this.store.dispatch(new GetCollections(organization.id));
    }
  }

  ngOnInit() {
    // read defaults
    const defaultGroupEdit = this.storageService.getLocalItem(StorageItemEnum.DefaultGroupEdit) === 'true';
    const defaultCollectionId = this.storageService.getLocalItem(StorageItemEnum.DefaultCollectionId);

    this.form = this.formBuilder.group({
      groupEditEnabled: [`${defaultGroupEdit}`, Validators.required],
      collectionId: [defaultCollectionId]
    });

    // write defaults
    this.form.valueChanges
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(({ groupEditEnabled, collectionId }) => {
        if (groupEditEnabled) {
          this.storageService.setLocalItem(StorageItemEnum.DefaultGroupEdit, groupEditEnabled);
        } else {
          this.storageService.removeLocalItem(StorageItemEnum.DefaultGroupEdit);
        }
        if (collectionId) {
          this.storageService.setLocalItem(StorageItemEnum.DefaultCollectionId, collectionId);
        } else {
          this.storageService.removeLocalItem(StorageItemEnum.DefaultCollectionId);
        }
      });

    // close on success
    this.actions
      .pipe(
        ofActionDispatched(CopyBookmarkSuccess),
        filter((action: CopyBookmarkSuccess) => action.sourceId === this.settings.bookmark.id && !!action.groupAccess),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((e) => {
        this.dialogRef.close(e.newId);
      });

    // notify on failure
    this.actions
      .pipe(
        ofActionDispatched(CopyBookmarkFailure),
        filter((action: CopyBookmarkFailure) => action.id === this.settings.bookmark.id),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((e) => {
        this.snackBar.open(`Adding ${this.settings.bookmark.title} to your team models has failed. Please try again`);
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  handleSubmit() {
    if (this.form.valid) {
      this.addTeamModel(this.settings.bookmark);
    }
  }

  addTeamModel(bookmark: BookmarkModel) {
    const { groupEditEnabled, collectionId } = this.form.value;
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    const collections = this.store.selectSnapshot(OrganizationsState.getCollections);

    // set group access    
    const groupAccess: BookmarkGroupModel = {
      group_id: organization.id,
      member_edit: groupEditEnabled === 'true',
      visible: true
    };

    // set collection
    const collection = collections.find(({ collection_id }) => `${collection_id}` === collectionId);

    // copy bookmark
    this.store.dispatch(new CopyBookmark(bookmark.id, <StoryType>bookmark.type, groupAccess, collection));
  }
 
}
